import { apiClient } from './client';
import {
  PurchaseOrder,
  PurchaseOrderAttachmentKind,
  PurchaseOrderPayload,
} from '../types/purchaseOrder';
import { SequentialCodeSuggestion } from '../types/sequentialCode';

export async function fetchPurchaseOrders(params?: {
  contractId?: number;
  keyword?: string;
}): Promise<PurchaseOrder[]> {
  const { data } = await apiClient.get<PurchaseOrder[]>('/purchase-orders', {
    params: {
      ...(params?.contractId != null ? { contractId: params.contractId } : {}),
      ...(params?.keyword?.trim() ? { q: params.keyword.trim() } : {}),
    },
  });
  return data;
}

export async function fetchNextPurchaseOrderNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/purchase-orders/suggestions/order-no'
  );
  return data;
}

export async function fetchPurchaseOrder(purchaseOrderId: number): Promise<PurchaseOrder> {
  const { data } = await apiClient.get<PurchaseOrder>(`/purchase-orders/${purchaseOrderId}`);
  return data;
}

export async function createPurchaseOrder(payload: PurchaseOrderPayload): Promise<PurchaseOrder> {
  const { data } = await apiClient.post<PurchaseOrder>('/purchase-orders', payload);
  return data;
}

export async function updatePurchaseOrder(
  purchaseOrderId: number,
  payload: Partial<PurchaseOrderPayload>
): Promise<PurchaseOrder> {
  const { data } = await apiClient.patch<PurchaseOrder>(
    `/purchase-orders/${purchaseOrderId}`,
    payload
  );
  return data;
}

export async function deletePurchaseOrder(purchaseOrderId: number): Promise<void> {
  await apiClient.delete(`/purchase-orders/${purchaseOrderId}`);
}

export async function approvePurchaseOrder(purchaseOrderId: number): Promise<PurchaseOrder> {
  const { data } = await apiClient.post<PurchaseOrder>(`/purchase-orders/${purchaseOrderId}/approve`);
  return data;
}

export async function revokePurchaseOrder(purchaseOrderId: number): Promise<PurchaseOrder> {
  const { data } = await apiClient.post<PurchaseOrder>(`/purchase-orders/${purchaseOrderId}/revoke`);
  return data;
}

export async function markPurchaseOrderOrdered(purchaseOrderId: number): Promise<PurchaseOrder> {
  const { data } = await apiClient.post<PurchaseOrder>(`/purchase-orders/${purchaseOrderId}/ordered`);
  return data;
}

export async function cancelPurchaseOrderOrdered(purchaseOrderId: number): Promise<PurchaseOrder> {
  const { data } = await apiClient.post<PurchaseOrder>(
    `/purchase-orders/${purchaseOrderId}/ordered/cancel`
  );
  return data;
}

export async function downloadPurchaseOrderPdf(
  purchaseOrderId: number,
  fileName?: string
): Promise<void> {
  try {
    const { data } = await apiClient.get<Blob>(`/purchase-orders/${purchaseOrderId}/pdf`, {
      responseType: 'blob',
    });
    if (data.type && data.type.includes('application/json')) {
      const text = await data.text();
      const parsed = JSON.parse(text) as { message?: string };
      throw new Error(parsed.message || 'PDF 다운로드에 실패했습니다.');
    }
    const url = window.URL.createObjectURL(data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName || `발주서_${purchaseOrderId}.pdf`;
    anchor.click();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    const blob = (err as { response?: { data?: Blob } })?.response?.data;
    if (blob instanceof Blob) {
      try {
        const text = await blob.text();
        const parsed = JSON.parse(text) as { message?: string };
        if (parsed.message) throw new Error(parsed.message);
      } catch (inner) {
        if (inner instanceof Error && inner.message && !inner.message.startsWith('Unexpected')) {
          throw inner;
        }
      }
    }
    if (err instanceof Error && err.message) throw err;
    throw err;
  }
}

export async function uploadPurchaseOrderAttachment(
  purchaseOrderId: number,
  file: File,
  kind: PurchaseOrderAttachmentKind = 'OTHER'
): Promise<PurchaseOrder> {
  const body = new FormData();
  body.append('file', file);
  const { data } = await apiClient.post<PurchaseOrder>(
    `/purchase-orders/${purchaseOrderId}/attachments`,
    body,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { kind },
    }
  );
  return data;
}

export async function deletePurchaseOrderAttachment(
  purchaseOrderId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/purchase-orders/${purchaseOrderId}/attachments/${attachmentId}`);
}

export async function downloadPurchaseOrderAttachment(
  purchaseOrderId: number,
  attachmentId: number
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/purchase-orders/${purchaseOrderId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  return data;
}
